/**
 * KnowledgeBaseModal — full-screen admin surface for the AI Diagnostic
 * Assistant's knowledge base.
 *
 * Users can:
 *   • Upload a document with rich metadata (category, manufacturer, family,
 *     model, year, doc type, version, effective date, source, description).
 *   • Browse every uploaded document with status, page count, chunk count,
 *     error codes extracted, and version-history links.
 *   • Deactivate, delete, or re-index any document.
 *   • Upload a replacement version (both old + new remain active per user
 *     choice; version history is threaded via supersededByDocumentId).
 *   • Manage the underlying taxonomy (categories / equipment types /
 *     manufacturers / families / models) inline so the metadata pickers
 *     have data to pick from.
 *
 * Trade-agnostic by design: every option list is loaded from the tenant's
 * own taxonomy — the UI itself contains zero HVAC/plumbing/electrical
 * keywords. New trades work without a code change.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import {
  AlertTriangle,
  Book,
  BookOpen,
  Check,
  CheckCircle2,
  ChevronDown,
  Clock,
  Download,
  FileText,
  Filter,
  History,
  Info,
  Loader2,
  Pencil,
  Plus,
  RefreshCw,
  Search,
  Trash2,
  Upload,
  X,
  Zap,
} from "lucide-react";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Textarea } from "./ui/textarea";
import { kbApi } from "../lib/api";
import RetrievalTesterPanel from "./RetrievalTesterPanel";

const DOC_TYPES = [
  { value: "service_manual", label: "Service manual" },
  { value: "repair_manual", label: "Repair manual" },
  { value: "installation_manual", label: "Installation manual" },
  { value: "troubleshooting_guide", label: "Troubleshooting guide" },
  { value: "technical_bulletin", label: "Technical bulletin" },
  { value: "specification", label: "Specification" },
  { value: "wiring_diagram", label: "Wiring diagram" },
  { value: "schematic", label: "Schematic" },
  { value: "error_code_doc", label: "Error-code doc" },
  { value: "parts_doc", label: "Parts doc" },
  { value: "diagnostic_procedure", label: "Diagnostic procedure" },
  { value: "maintenance_procedure", label: "Maintenance procedure" },
  { value: "training_doc", label: "Training doc" },
  { value: "manual", label: "Other manual" },
];

const DOC_TYPE_LABEL = Object.fromEntries(DOC_TYPES.map((t) => [t.value, t.label]));

// --- Small helpers ------------------------------------------------------------
const fmtBytes = (n) => {
  if (!n && n !== 0) return "";
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / (1024 * 1024)).toFixed(1)} MB`;
};

const StatusPill = ({ status, error }) => {
  const map = {
    indexed: { bg: "bg-emerald-50", text: "text-emerald-700", icon: <CheckCircle2 className="h-3 w-3" />, label: "Indexed" },
    processing: { bg: "bg-blue-50", text: "text-blue-700", icon: <Loader2 className="h-3 w-3 animate-spin" />, label: "Processing" },
    pending: { bg: "bg-slate-50", text: "text-slate-600", icon: <Clock className="h-3 w-3" />, label: "Pending" },
    error: { bg: "bg-rose-50", text: "text-rose-700", icon: <AlertTriangle className="h-3 w-3" />, label: "Error" },
  };
  const cfg = map[status] || map.pending;
  return (
    <span
      className={`inline-flex items-center gap-1 rounded-md px-2 py-0.5 text-[11px] font-medium ${cfg.bg} ${cfg.text}`}
      title={error || cfg.label}
      data-testid={`kb-status-${status}`}
    >
      {cfg.icon}
      {cfg.label}
    </span>
  );
};

// Simple native select styled to match — keeps the file dependency-free.
const NativeSelect = ({ value, onChange, options, placeholder, disabled, testId }) => (
  <div className="relative">
    <select
      value={value ?? ""}
      onChange={(e) => onChange(e.target.value || null)}
      disabled={disabled}
      className="w-full appearance-none rounded-md border border-slate-200 bg-white px-3 pr-8 py-2 text-[13px] text-slate-800 outline-none focus:border-blue-400 disabled:bg-slate-50 disabled:text-slate-400"
      data-testid={testId}
    >
      <option value="">{placeholder || "— None —"}</option>
      {options.map((o) => (
        <option key={o.value} value={o.value}>
          {o.label}
        </option>
      ))}
    </select>
    <ChevronDown className="pointer-events-none absolute right-2 top-2.5 h-4 w-4 text-slate-400" />
  </div>
);

// Taxonomy sub-tab button — extracted so React doesn't recreate the type on every render.
const TaxonomyTabBtn = ({ active, onClick, label, count, testId }) => (
  <button
    onClick={onClick}
    className={`px-3 py-1.5 rounded-md text-[12px] font-medium transition-colors ${
      active ? "bg-blue-600 text-white" : "text-slate-600 hover:bg-slate-100"
    }`}
    data-testid={testId}
  >
    {label} <span className={active ? "text-blue-100" : "text-slate-400"}>·{count}</span>
  </button>
);

// --- Upload dialog ------------------------------------------------------------
function UploadDialog({ open, onClose, taxonomy, prefill, existingDocs, onUploaded }) {
  const [file, setFile] = useState(null);
  const [title, setTitle] = useState("");
  const [docType, setDocType] = useState("service_manual");
  const [categoryId, setCategoryId] = useState(prefill?.categoryId || null);
  const [equipmentTypeId, setEquipmentTypeId] = useState(prefill?.equipmentTypeId || null);
  const [manufacturerId, setManufacturerId] = useState(prefill?.manufacturerId || null);
  const [familyId, setFamilyId] = useState(null);
  const [modelIds, setModelIds] = useState([]);
  const [year, setYear] = useState("");
  const [version, setVersion] = useState("");
  const [effectiveDate, setEffectiveDate] = useState("");
  const [source, setSource] = useState("");
  const [description, setDescription] = useState("");
  const [supersedesDocumentId, setSupersedesDocumentId] = useState("");
  const [progress, setProgress] = useState(0);
  const [busy, setBusy] = useState(false);

  // Reset every field on open — but honour any prefill (e.g. from "Replace" button).
  useEffect(() => {
    if (!open) return;
    setFile(null);
    setTitle(prefill?.title || "");
    setDocType(prefill?.docType || "service_manual");
    setCategoryId(prefill?.categoryId || null);
    setEquipmentTypeId(prefill?.equipmentTypeId || null);
    setManufacturerId(prefill?.manufacturerId || null);
    setFamilyId(prefill?.familyId || null);
    setModelIds(prefill?.modelIds || []);
    setYear(prefill?.year || "");
    setVersion("");
    setEffectiveDate("");
    setSource(prefill?.source || "");
    setDescription(prefill?.description || "");
    setSupersedesDocumentId(prefill?.supersedesDocumentId || "");
    setProgress(0);
  }, [open, prefill]);

  // Filtered sub-taxonomy lists based on the active scope selections.
  const equipmentOptions = useMemo(
    () => taxonomy.equipmentTypes.filter((e) => !categoryId || e.categoryId === categoryId),
    [taxonomy.equipmentTypes, categoryId],
  );
  const manufacturerOptions = useMemo(
    () => taxonomy.manufacturers.filter((m) => !categoryId || m.categoryId === categoryId),
    [taxonomy.manufacturers, categoryId],
  );
  const familyOptions = useMemo(
    () =>
      taxonomy.families.filter(
        (f) =>
          (!manufacturerId || f.manufacturerId === manufacturerId) &&
          (!equipmentTypeId || f.equipmentTypeId === equipmentTypeId),
      ),
    [taxonomy.families, manufacturerId, equipmentTypeId],
  );
  const modelOptions = useMemo(
    () =>
      taxonomy.models.filter(
        (m) =>
          (!manufacturerId || m.manufacturerId === manufacturerId) &&
          (!equipmentTypeId || m.equipmentTypeId === equipmentTypeId),
      ),
    [taxonomy.models, manufacturerId, equipmentTypeId],
  );

  const supersedeCandidates = useMemo(() => {
    // Suggest docs that share at least a categoryId/manufacturerId with the current draft.
    return existingDocs.filter(
      (d) => (!categoryId || d.categoryId === categoryId) && (!manufacturerId || d.manufacturerId === manufacturerId),
    );
  }, [existingDocs, categoryId, manufacturerId]);

  const submit = async () => {
    if (!file) {
      toast.error("Pick a file first");
      return;
    }
    if (!title.trim()) {
      toast.error("Give this document a title");
      return;
    }
    setBusy(true);
    setProgress(0);
    try {
      const res = await kbApi.documents.upload(
        {
          title: title.trim(),
          docType,
          categoryId,
          equipmentTypeId,
          manufacturerId,
          familyId,
          modelIds,
          year: year ? Number(year) : "",
          version,
          effectiveDate,
          source,
          description,
          supersedesDocumentId,
        },
        file,
        setProgress,
      );
      const doc = res?.document;
      if (doc?.status === "error") {
        toast.error(`Ingest error: ${doc.indexError || "See document detail."}`);
      } else {
        toast.success(`Indexed ${doc?.chunkCount ?? 0} chunks across ${doc?.pages ?? 0} pages`);
      }
      onUploaded(doc);
      onClose();
    } catch (ex) {
      toast.error(ex?.response?.data?.detail || "Upload failed");
    } finally {
      setBusy(false);
    }
  };

  if (!open) return null;
  return (
    <div className="fixed inset-0 z-[80] flex items-center justify-center bg-slate-900/60 backdrop-blur-sm p-4">
      <div className="w-full max-w-3xl max-h-[90vh] overflow-hidden rounded-xl bg-white shadow-2xl flex flex-col">
        <div className="flex items-center justify-between px-5 py-3.5 border-b border-slate-200">
          <div>
            <div className="text-[15px] font-semibold text-slate-900">Upload knowledge document</div>
            <div className="text-[12px] text-slate-500">Metadata drives the AI&apos;s retrieval — fill what you can.</div>
          </div>
          <button
            className="h-8 w-8 rounded-md hover:bg-slate-100 flex items-center justify-center text-slate-500"
            onClick={onClose}
            data-testid="kb-upload-close"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-5 space-y-4">
          {/* File picker */}
          <div>
            <Label className="text-[12px] font-medium text-slate-700">File</Label>
            <label
              className={`mt-1 flex items-center justify-between gap-3 rounded-lg border-2 border-dashed px-4 py-4 cursor-pointer transition-colors ${
                file ? "border-emerald-300 bg-emerald-50/40" : "border-slate-200 bg-slate-50/40 hover:border-blue-300"
              }`}
              data-testid="kb-upload-file-picker"
            >
              <div className="flex items-center gap-3 min-w-0">
                <div className="h-9 w-9 rounded-md bg-white border border-slate-200 flex items-center justify-center shrink-0">
                  <Upload className="h-4 w-4 text-slate-500" />
                </div>
                <div className="min-w-0">
                  {file ? (
                    <>
                      <div className="text-[13px] font-medium text-slate-800 truncate">{file.name}</div>
                      <div className="text-[11px] text-slate-500">{fmtBytes(file.size)}</div>
                    </>
                  ) : (
                    <>
                      <div className="text-[13px] font-medium text-slate-700">Drop a file or click to pick</div>
                      <div className="text-[11px] text-slate-500">PDF, DOCX, TXT, MD, CSV, XLSX — up to 40 MB</div>
                    </>
                  )}
                </div>
              </div>
              <input
                type="file"
                hidden
                accept=".pdf,.docx,.txt,.md,.csv,.xlsx,.xlsm"
                onChange={(e) => setFile(e.target.files?.[0] || null)}
              />
            </label>
          </div>

          {/* Row 1: Title + Doc Type */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-[12px] font-medium text-slate-700">Title</Label>
              <Input
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                placeholder="Carrier 58MCA Service Manual"
                className="mt-1 h-9 text-[13px]"
                data-testid="kb-upload-title"
              />
            </div>
            <div>
              <Label className="text-[12px] font-medium text-slate-700">Document type</Label>
              <div className="mt-1">
                <NativeSelect
                  value={docType}
                  onChange={(v) => setDocType(v || "manual")}
                  options={DOC_TYPES}
                  testId="kb-upload-doctype"
                />
              </div>
            </div>
          </div>

          {/* Row 2: Scope pickers */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-[12px] font-medium text-slate-700">Service category</Label>
              <div className="mt-1">
                <NativeSelect
                  value={categoryId}
                  onChange={(v) => {
                    setCategoryId(v);
                    setEquipmentTypeId(null);
                    setManufacturerId(null);
                    setFamilyId(null);
                    setModelIds([]);
                  }}
                  options={taxonomy.categories.map((c) => ({ value: c.id, label: c.name }))}
                  placeholder="— Any / global —"
                  testId="kb-upload-category"
                />
              </div>
            </div>
            <div>
              <Label className="text-[12px] font-medium text-slate-700">Equipment type</Label>
              <div className="mt-1">
                <NativeSelect
                  value={equipmentTypeId}
                  onChange={(v) => {
                    setEquipmentTypeId(v);
                    setFamilyId(null);
                    setModelIds([]);
                  }}
                  options={equipmentOptions.map((e) => ({ value: e.id, label: e.name }))}
                  placeholder={categoryId ? "— Any equipment —" : "Pick a category first"}
                  disabled={!categoryId}
                  testId="kb-upload-equipment"
                />
              </div>
            </div>
            <div>
              <Label className="text-[12px] font-medium text-slate-700">Manufacturer</Label>
              <div className="mt-1">
                <NativeSelect
                  value={manufacturerId}
                  onChange={(v) => {
                    setManufacturerId(v);
                    setFamilyId(null);
                    setModelIds([]);
                  }}
                  options={manufacturerOptions.map((m) => ({ value: m.id, label: m.name }))}
                  placeholder={categoryId ? "— Any manufacturer —" : "Pick a category first"}
                  disabled={!categoryId}
                  testId="kb-upload-manufacturer"
                />
              </div>
            </div>
            <div>
              <Label className="text-[12px] font-medium text-slate-700">Family / Series (optional)</Label>
              <div className="mt-1">
                <NativeSelect
                  value={familyId}
                  onChange={setFamilyId}
                  options={familyOptions.map((f) => ({ value: f.id, label: f.name }))}
                  placeholder={familyOptions.length ? "— None —" : "No families for this mfr/type"}
                  disabled={!familyOptions.length}
                  testId="kb-upload-family"
                />
              </div>
            </div>
          </div>

          {/* Row 3: models multiselect + year */}
          <div className="grid grid-cols-3 gap-3">
            <div className="col-span-2">
              <Label className="text-[12px] font-medium text-slate-700">Models</Label>
              <div className="mt-1 max-h-32 overflow-y-auto rounded-md border border-slate-200 p-2 flex flex-wrap gap-1.5 bg-white">
                {modelOptions.length === 0 && (
                  <div className="text-[12px] text-slate-400 px-1 py-0.5">
                    {equipmentTypeId && manufacturerId ? "No models — add some in the Taxonomy tab" : "Pick equipment + manufacturer first"}
                  </div>
                )}
                {modelOptions.map((m) => {
                  const on = modelIds.includes(m.id);
                  return (
                    <button
                      key={m.id}
                      type="button"
                      onClick={() =>
                        setModelIds((cur) => (on ? cur.filter((x) => x !== m.id) : [...cur, m.id]))
                      }
                      className={`px-2 py-1 rounded-md text-[11.5px] font-medium border transition-colors ${
                        on
                          ? "bg-blue-600 text-white border-blue-600"
                          : "bg-slate-50 text-slate-700 border-slate-200 hover:border-blue-300"
                      }`}
                      data-testid={`kb-upload-model-${m.id}`}
                    >
                      {m.name}
                    </button>
                  );
                })}
              </div>
              <div className="text-[10.5px] text-slate-500 mt-0.5">
                Leave empty for family / manufacturer / category-level documents.
              </div>
            </div>
            <div>
              <Label className="text-[12px] font-medium text-slate-700">Model year</Label>
              <Input
                type="number"
                min={1970}
                max={2100}
                value={year}
                onChange={(e) => setYear(e.target.value)}
                placeholder="2024"
                className="mt-1 h-9 text-[13px]"
                data-testid="kb-upload-year"
              />
            </div>
          </div>

          {/* Row 4: Version + Effective date + Source */}
          <div className="grid grid-cols-3 gap-3">
            <div>
              <Label className="text-[12px] font-medium text-slate-700">Document version</Label>
              <Input
                value={version}
                onChange={(e) => setVersion(e.target.value)}
                placeholder="Rev C"
                className="mt-1 h-9 text-[13px]"
                data-testid="kb-upload-version"
              />
            </div>
            <div>
              <Label className="text-[12px] font-medium text-slate-700">Effective date</Label>
              <Input
                type="date"
                value={effectiveDate}
                onChange={(e) => setEffectiveDate(e.target.value)}
                className="mt-1 h-9 text-[13px]"
                data-testid="kb-upload-effective"
              />
            </div>
            <div>
              <Label className="text-[12px] font-medium text-slate-700">Source</Label>
              <Input
                value={source}
                onChange={(e) => setSource(e.target.value)}
                placeholder="Manufacturer portal"
                className="mt-1 h-9 text-[13px]"
                data-testid="kb-upload-source"
              />
            </div>
          </div>

          {/* Description */}
          <div>
            <Label className="text-[12px] font-medium text-slate-700">Description</Label>
            <Textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Short description used to help admins find this doc later."
              className="mt-1 min-h-[64px] text-[13px]"
              data-testid="kb-upload-description"
            />
          </div>

          {/* Supersedes picker — optional */}
          <div>
            <Label className="text-[12px] font-medium text-slate-700 flex items-center gap-1.5">
              <History className="h-3.5 w-3.5" /> Supersedes an existing doc? (optional)
            </Label>
            <div className="mt-1">
              <NativeSelect
                value={supersedesDocumentId}
                onChange={setSupersedesDocumentId}
                options={supersedeCandidates.map((d) => ({
                  value: d.id,
                  label: `${d.title}${d.version ? ` (${d.version})` : ""}`,
                }))}
                placeholder="— This is a brand new doc —"
                testId="kb-upload-supersedes"
              />
              <div className="text-[10.5px] text-slate-500 mt-0.5">
                Both versions stay active — the older doc will show a &ldquo;Superseded by&rdquo; link so admins can pick.
              </div>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="px-5 py-3 border-t border-slate-100 flex items-center justify-between">
          <div className="text-[11px] text-slate-500 min-w-[80px]">
            {busy && progress > 0 && progress < 100 ? `Uploading… ${progress}%` : ""}
            {busy && progress === 100 ? "Extracting text…" : ""}
          </div>
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" onClick={onClose} disabled={busy} data-testid="kb-upload-cancel">
              Cancel
            </Button>
            <Button
              size="sm"
              onClick={submit}
              disabled={busy || !file || !title.trim()}
              className="bg-blue-600 hover:bg-blue-700 text-white"
              data-testid="kb-upload-submit"
            >
              {busy ? <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" /> : <Upload className="h-3.5 w-3.5 mr-1.5" />}
              Upload & index
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}

// --- Taxonomy inline editor ---------------------------------------------------
function TaxonomyPanel({ taxonomy, refresh }) {
  const [tab, setTab] = useState("categories");
  const [newValue, setNewValue] = useState({});
  const [selectedCategoryId, setSelectedCategoryId] = useState(null);
  const [selectedManufacturerId, setSelectedManufacturerId] = useState(null);
  const [selectedEquipmentTypeId, setSelectedEquipmentTypeId] = useState(null);

  const submit = async () => {
    const name = (newValue.name || "").trim();
    if (!name) return;
    try {
      if (tab === "categories") {
        await kbApi.categories.create({ name });
      } else if (tab === "equipment-types") {
        if (!selectedCategoryId) return toast.error("Pick a category first");
        await kbApi.equipmentTypes.create({ name, categoryId: selectedCategoryId });
      } else if (tab === "manufacturers") {
        if (!selectedCategoryId) return toast.error("Pick a category first");
        await kbApi.manufacturers.create({ name, categoryId: selectedCategoryId });
      } else if (tab === "families") {
        if (!selectedManufacturerId || !selectedEquipmentTypeId)
          return toast.error("Pick a manufacturer + equipment first");
        await kbApi.families.create({
          name,
          manufacturerId: selectedManufacturerId,
          equipmentTypeId: selectedEquipmentTypeId,
        });
      } else if (tab === "models") {
        if (!selectedManufacturerId || !selectedEquipmentTypeId)
          return toast.error("Pick a manufacturer + equipment first");
        await kbApi.models.create({
          name,
          manufacturerId: selectedManufacturerId,
          equipmentTypeId: selectedEquipmentTypeId,
        });
      }
      setNewValue({});
      toast.success("Added");
      refresh();
    } catch (ex) {
      toast.error(ex?.response?.data?.detail || "Failed to add");
    }
  };

  const remove = async (kind, id) => {
    if (!window.confirm("Delete this entry? Anything that hangs off it will be removed too.")) return;
    try {
      await kbApi[kind].remove(id);
      toast.success("Removed");
      refresh();
    } catch (ex) {
      toast.error(ex?.response?.data?.detail || "Failed to remove");
    }
  };

  const items = {
    categories: taxonomy.categories,
    "equipment-types": taxonomy.equipmentTypes.filter((e) => !selectedCategoryId || e.categoryId === selectedCategoryId),
    manufacturers: taxonomy.manufacturers.filter((m) => !selectedCategoryId || m.categoryId === selectedCategoryId),
    families: taxonomy.families.filter(
      (f) =>
        (!selectedManufacturerId || f.manufacturerId === selectedManufacturerId) &&
        (!selectedEquipmentTypeId || f.equipmentTypeId === selectedEquipmentTypeId),
    ),
    models: taxonomy.models.filter(
      (m) =>
        (!selectedManufacturerId || m.manufacturerId === selectedManufacturerId) &&
        (!selectedEquipmentTypeId || m.equipmentTypeId === selectedEquipmentTypeId),
    ),
  };
  const removeKindMap = {
    categories: "categories",
    "equipment-types": "equipmentTypes",
    manufacturers: "manufacturers",
    families: "families",
    models: "models",
  };

  return (
    <div className="space-y-3">
      <div className="flex items-center gap-1.5 flex-wrap">
        <TaxonomyTabBtn active={tab === "categories"} onClick={() => setTab("categories")} label="Categories" count={taxonomy.categories.length} testId="kb-taxonomy-tab-categories" />
        <TaxonomyTabBtn active={tab === "equipment-types"} onClick={() => setTab("equipment-types")} label="Equipment types" count={taxonomy.equipmentTypes.length} testId="kb-taxonomy-tab-equipment-types" />
        <TaxonomyTabBtn active={tab === "manufacturers"} onClick={() => setTab("manufacturers")} label="Manufacturers" count={taxonomy.manufacturers.length} testId="kb-taxonomy-tab-manufacturers" />
        <TaxonomyTabBtn active={tab === "families"} onClick={() => setTab("families")} label="Families" count={taxonomy.families.length} testId="kb-taxonomy-tab-families" />
        <TaxonomyTabBtn active={tab === "models"} onClick={() => setTab("models")} label="Models" count={taxonomy.models.length} testId="kb-taxonomy-tab-models" />
      </div>

      {/* Scope pickers for the sub-lists */}
      {tab !== "categories" && (
        <div className="grid grid-cols-2 gap-3">
          <div>
            <Label className="text-[11px] text-slate-500">Filter by category</Label>
            <NativeSelect
              value={selectedCategoryId}
              onChange={setSelectedCategoryId}
              options={taxonomy.categories.map((c) => ({ value: c.id, label: c.name }))}
              placeholder="— All categories —"
              testId="kb-taxonomy-filter-category"
            />
          </div>
          {(tab === "families" || tab === "models") && (
            <>
              <div>
                <Label className="text-[11px] text-slate-500">Manufacturer</Label>
                <NativeSelect
                  value={selectedManufacturerId}
                  onChange={setSelectedManufacturerId}
                  options={taxonomy.manufacturers
                    .filter((m) => !selectedCategoryId || m.categoryId === selectedCategoryId)
                    .map((m) => ({ value: m.id, label: m.name }))}
                  placeholder="— Any manufacturer —"
                  testId="kb-taxonomy-filter-manufacturer"
                />
              </div>
              <div>
                <Label className="text-[11px] text-slate-500">Equipment type</Label>
                <NativeSelect
                  value={selectedEquipmentTypeId}
                  onChange={setSelectedEquipmentTypeId}
                  options={taxonomy.equipmentTypes
                    .filter((e) => !selectedCategoryId || e.categoryId === selectedCategoryId)
                    .map((e) => ({ value: e.id, label: e.name }))}
                  placeholder="— Any equipment —"
                  testId="kb-taxonomy-filter-equipment"
                />
              </div>
            </>
          )}
        </div>
      )}

      {/* Add-new row */}
      <div className="flex items-center gap-2">
        <Input
          value={newValue.name || ""}
          onChange={(e) => setNewValue({ name: e.target.value })}
          placeholder={`New ${tab.replace("-", " ").slice(0, -1)}…`}
          className="h-9 text-[13px]"
          data-testid="kb-taxonomy-new-input"
          onKeyDown={(e) => e.key === "Enter" && submit()}
        />
        <Button
          size="sm"
          onClick={submit}
          className="h-9 bg-blue-600 hover:bg-blue-700 text-white"
          data-testid="kb-taxonomy-add-btn"
        >
          <Plus className="h-3.5 w-3.5 mr-1" /> Add
        </Button>
      </div>

      {/* Item list */}
      <div className="border border-slate-200 rounded-md overflow-hidden">
        {items[tab].length === 0 ? (
          <div className="p-6 text-center text-[12px] text-slate-400">Nothing here yet — add your first {tab.slice(0, -1)}.</div>
        ) : (
          <ul className="divide-y divide-slate-100">
            {items[tab].map((it) => (
              <li key={it.id} className="flex items-center justify-between px-3 py-2 hover:bg-slate-50">
                <div className="text-[13px] text-slate-800">{it.name}</div>
                <button
                  onClick={() => remove(removeKindMap[tab], it.id)}
                  className="text-slate-400 hover:text-rose-600 p-1"
                  title="Delete"
                  data-testid={`kb-taxonomy-remove-${it.id}`}
                >
                  <Trash2 className="h-3.5 w-3.5" />
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}

// --- Document detail (opens on row-click) ------------------------------------
function DocumentDetail({ doc, taxonomy, onClose, onChanged }) {
  const [tab, setTab] = useState("meta");
  const [detail, setDetail] = useState(null);
  const [chunks, setChunks] = useState([]);
  const [conflicts, setConflicts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!doc) return;
    setLoading(true);
    Promise.all([
      kbApi.documents.detail(doc.id),
      kbApi.documents.chunks(doc.id),
      kbApi.documents.conflicts(doc.id),
    ])
      .then(([d, chunkList, conflictList]) => {
        setDetail(d);
        setChunks(chunkList || []);
        setConflicts(conflictList || []);
      })
      .catch(() => toast.error("Failed to load document"))
      .finally(() => setLoading(false));
  }, [doc]);

  const toggleActive = async () => {
    try {
      const next = !detail.document.active;
      await kbApi.documents.patch(doc.id, { active: next });
      toast.success(next ? "Activated" : "Deactivated");
      onChanged();
      onClose();
    } catch (ex) {
      toast.error(ex?.response?.data?.detail || "Failed");
    }
  };

  const reindex = async () => {
    try {
      await kbApi.documents.reindex(doc.id);
      toast.success("Reindexed");
      onChanged();
      onClose();
    } catch (ex) {
      toast.error(ex?.response?.data?.detail || "Failed to reindex");
    }
  };

  const remove = async () => {
    if (!window.confirm("Permanently delete this document and all its chunks?")) return;
    try {
      await kbApi.documents.remove(doc.id);
      toast.success("Deleted");
      onChanged();
      onClose();
    } catch (ex) {
      toast.error(ex?.response?.data?.detail || "Failed to delete");
    }
  };

  const nameOf = (kind, id) => {
    if (!id) return "—";
    const item = (taxonomy[kind] || []).find((x) => x.id === id);
    return item ? item.name : id;
  };

  if (!doc) return null;
  const d = detail?.document;
  return (
    <div className="fixed inset-0 z-[80] flex items-center justify-center bg-slate-900/60 backdrop-blur-sm p-4">
      <div className="w-full max-w-4xl max-h-[90vh] overflow-hidden rounded-xl bg-white shadow-2xl flex flex-col">
        <div className="flex items-start justify-between px-5 py-3.5 border-b border-slate-200">
          <div className="min-w-0 pr-4">
            <div className="text-[15px] font-semibold text-slate-900 truncate" data-testid="kb-detail-title">{doc.title}</div>
            <div className="text-[12px] text-slate-500 flex items-center gap-2 mt-0.5">
              <span>{DOC_TYPE_LABEL[doc.docType] || doc.docType}</span>
              {doc.version && <span>· v{doc.version}</span>}
              {doc.effectiveDate && <span>· effective {doc.effectiveDate}</span>}
              <StatusPill status={doc.status} error={doc.indexError} />
            </div>
          </div>
          <button className="h-8 w-8 rounded-md hover:bg-slate-100 flex items-center justify-center text-slate-500" onClick={onClose} data-testid="kb-detail-close">
            <X className="h-4 w-4" />
          </button>
        </div>

        <div className="border-b border-slate-100 px-5">
          <div className="flex items-center gap-1">
            {[
              { id: "meta", label: "Metadata", icon: <Info className="h-3.5 w-3.5" /> },
              { id: "chunks", label: `Chunks (${chunks.length})`, icon: <BookOpen className="h-3.5 w-3.5" /> },
              { id: "conflicts", label: `Conflicts (${conflicts.length})`, icon: <AlertTriangle className="h-3.5 w-3.5" /> },
            ].map((t) => (
              <button
                key={t.id}
                onClick={() => setTab(t.id)}
                className={`flex items-center gap-1.5 px-3 py-2 text-[12.5px] font-medium border-b-2 -mb-px ${
                  tab === t.id ? "border-blue-600 text-blue-700" : "border-transparent text-slate-500 hover:text-slate-700"
                }`}
                data-testid={`kb-detail-tab-${t.id}`}
              >
                {t.icon}
                {t.label}
              </button>
            ))}
          </div>
        </div>

        <div className="flex-1 overflow-y-auto p-5">
          {loading ? (
            <div className="flex items-center justify-center py-16 text-slate-400">
              <Loader2 className="h-5 w-5 animate-spin" />
            </div>
          ) : tab === "meta" ? (
            <div className="grid grid-cols-2 gap-x-6 gap-y-3 text-[13px]">
              {[
                ["Filename", d?.filename],
                ["File size", fmtBytes(d?.fileSize)],
                ["Pages", d?.pages],
                ["Chunks", d?.chunkCount],
                ["Uploaded by", d?.uploadedBy],
                ["Uploaded at", d?.uploadedAt?.slice(0, 19).replace("T", " ")],
                ["Category", nameOf("categories", d?.categoryId)],
                ["Equipment type", nameOf("equipmentTypes", d?.equipmentTypeId)],
                ["Manufacturer", nameOf("manufacturers", d?.manufacturerId)],
                ["Family", nameOf("families", d?.familyId)],
                ["Year", d?.year || "—"],
                ["Source", d?.source || "—"],
              ].map(([label, val]) => (
                <div key={label} className="flex flex-col">
                  <span className="text-[10.5px] font-medium uppercase tracking-wider text-slate-400">{label}</span>
                  <span className="text-slate-800">{val ?? "—"}</span>
                </div>
              ))}
              <div className="col-span-2 flex flex-col">
                <span className="text-[10.5px] font-medium uppercase tracking-wider text-slate-400">Models</span>
                <div className="flex flex-wrap gap-1.5 mt-1">
                  {(d?.modelIds || []).length ? (
                    d.modelIds.map((mid) => (
                      <span key={mid} className="px-2 py-0.5 bg-slate-100 rounded-md text-[11.5px] text-slate-700">
                        {nameOf("models", mid)}
                      </span>
                    ))
                  ) : (
                    <span className="text-slate-400 text-[12px]">— No model scope —</span>
                  )}
                </div>
              </div>
              <div className="col-span-2 flex flex-col">
                <span className="text-[10.5px] font-medium uppercase tracking-wider text-slate-400">Description</span>
                <span className="text-slate-800 whitespace-pre-wrap">{d?.description || "—"}</span>
              </div>
              <div className="col-span-2 flex flex-col">
                <span className="text-[10.5px] font-medium uppercase tracking-wider text-slate-400">Extracted signals</span>
                <div className="flex flex-wrap gap-1.5 mt-1">
                  {Object.entries(d?.sectionCounts || {}).map(([k, v]) => (
                    <span key={k} className="px-2 py-0.5 bg-blue-50 text-blue-700 rounded-md text-[11px]">{k}: {v}</span>
                  ))}
                  {(d?.errorCodes || []).slice(0, 12).map((c) => (
                    <span key={c} className="px-2 py-0.5 bg-rose-50 text-rose-700 rounded-md text-[11px]">{c}</span>
                  ))}
                </div>
              </div>
              {d?.supersededByDocumentId && (
                <div className="col-span-2 rounded-md bg-amber-50 border border-amber-200 text-amber-800 px-3 py-2 text-[12px]">
                  <History className="inline h-3.5 w-3.5 mr-1.5" />
                  This document was superseded — a newer version exists (id: <code>{d.supersededByDocumentId}</code>).
                </div>
              )}
            </div>
          ) : tab === "chunks" ? (
            <div className="space-y-2">
              {chunks.map((c) => (
                <div key={c.id} className="rounded-md border border-slate-200 bg-white p-3">
                  <div className="flex items-center gap-2 mb-1.5 text-[11px]">
                    <span className="font-medium text-slate-600">Page {c.page}</span>
                    {c.section && <span className="text-slate-400">·</span>}
                    {c.section && <span className="text-slate-500 truncate">{c.section}</span>}
                    <span className="ml-auto px-1.5 py-0.5 bg-blue-50 text-blue-700 rounded text-[10.5px]">{c.sectionType}</span>
                    {(c.errorCodes || []).map((code) => (
                      <span key={code} className="px-1.5 py-0.5 bg-rose-50 text-rose-700 rounded text-[10.5px]">{code}</span>
                    ))}
                  </div>
                  <div className="text-[12.5px] text-slate-700 whitespace-pre-wrap">{c.text}</div>
                </div>
              ))}
              {chunks.length === 0 && <div className="text-center py-10 text-slate-400 text-[13px]">No chunks — ingest may have failed. Try reindex.</div>}
            </div>
          ) : (
            <div className="space-y-2">
              {conflicts.length === 0 && (
                <div className="text-center py-10 text-slate-400 text-[13px]">
                  <CheckCircle2 className="inline h-4 w-4 text-emerald-500 mr-1" />
                  No spec conflicts detected with other active documents.
                </div>
              )}
              {conflicts.map((c, i) => (
                <div key={i} className="rounded-md border border-rose-200 bg-rose-50/40 p-3">
                  <div className="text-[12px] font-medium text-rose-800 mb-1.5">
                    <AlertTriangle className="inline h-3.5 w-3.5 mr-1" />
                    Conflict on <b>{c.unit}</b>
                  </div>
                  <div className="grid grid-cols-2 gap-3 text-[12px]">
                    <div className="rounded bg-white border border-rose-100 p-2">
                      <div className="font-medium text-slate-700 truncate">{c.docA.title || c.docA.id}</div>
                      <div className="text-slate-500">Range: {c.docA.range?.join(" – ")}</div>
                    </div>
                    <div className="rounded bg-white border border-rose-100 p-2">
                      <div className="font-medium text-slate-700 truncate">{c.docB.title || c.docB.id}</div>
                      <div className="text-slate-500">Range: {c.docB.range?.join(" – ")}</div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="border-t border-slate-100 px-5 py-3 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <a
              href={kbApi.documents.downloadUrl(doc.id)}
              target="_blank"
              rel="noreferrer"
              className="inline-flex items-center gap-1.5 text-[12px] text-blue-700 hover:underline"
              data-testid="kb-detail-download"
            >
              <Download className="h-3.5 w-3.5" /> Download original
            </a>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" onClick={reindex} data-testid="kb-detail-reindex">
              <RefreshCw className="h-3.5 w-3.5 mr-1.5" /> Reindex
            </Button>
            <Button variant="outline" size="sm" onClick={toggleActive} data-testid="kb-detail-toggle-active">
              {d?.active ? "Deactivate" : "Activate"}
            </Button>
            <Button variant="outline" size="sm" onClick={remove} className="text-rose-600 hover:bg-rose-50" data-testid="kb-detail-delete">
              <Trash2 className="h-3.5 w-3.5 mr-1.5" /> Delete
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}

// --- Main modal ---------------------------------------------------------------
export default function KnowledgeBaseModal({ open, onClose }) {
  const [tab, setTab] = useState("documents");
  const [docs, setDocs] = useState([]);
  const [taxonomy, setTaxonomy] = useState({
    categories: [],
    equipmentTypes: [],
    manufacturers: [],
    families: [],
    models: [],
  });
  const [loading, setLoading] = useState(true);
  const [uploadOpen, setUploadOpen] = useState(false);
  const [uploadPrefill, setUploadPrefill] = useState(null);
  const [detailDoc, setDetailDoc] = useState(null);
  const [search, setSearch] = useState("");
  const [filter, setFilter] = useState({ categoryId: "", manufacturerId: "", docType: "", status: "" });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [d, c, e, m, f, mo] = await Promise.all([
        kbApi.documents.list(),
        kbApi.categories.list(),
        kbApi.equipmentTypes.list(),
        kbApi.manufacturers.list(),
        kbApi.families.list(),
        kbApi.models.list(),
      ]);
      setDocs(d || []);
      setTaxonomy({ categories: c || [], equipmentTypes: e || [], manufacturers: m || [], families: f || [], models: mo || [] });
    } catch (ex) {
      toast.error("Failed to load knowledge base");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (open) load();
  }, [open, load]);

  const filteredDocs = useMemo(() => {
    const q = search.trim().toLowerCase();
    return docs.filter((d) => {
      if (filter.categoryId && d.categoryId !== filter.categoryId) return false;
      if (filter.manufacturerId && d.manufacturerId !== filter.manufacturerId) return false;
      if (filter.docType && d.docType !== filter.docType) return false;
      if (filter.status && d.status !== filter.status) return false;
      if (!q) return true;
      return (
        (d.title || "").toLowerCase().includes(q) ||
        (d.filename || "").toLowerCase().includes(q) ||
        (d.version || "").toLowerCase().includes(q) ||
        (d.source || "").toLowerCase().includes(q)
      );
    });
  }, [docs, search, filter]);

  const nameOf = (kind, id) => {
    if (!id) return "—";
    const item = (taxonomy[kind] || []).find((x) => x.id === id);
    return item ? item.name : id;
  };

  const openReplace = (doc) => {
    setUploadPrefill({
      title: doc.title,
      docType: doc.docType,
      categoryId: doc.categoryId,
      equipmentTypeId: doc.equipmentTypeId,
      manufacturerId: doc.manufacturerId,
      familyId: doc.familyId,
      modelIds: doc.modelIds || [],
      year: doc.year || "",
      source: doc.source || "",
      description: doc.description || "",
      supersedesDocumentId: doc.id,
    });
    setUploadOpen(true);
  };

  if (!open) return null;
  return (
    <div className="fixed inset-0 z-[70] flex items-center justify-center bg-slate-900/60 backdrop-blur-sm p-4">
      <div className="w-full max-w-6xl h-[92vh] bg-white rounded-xl shadow-2xl flex flex-col overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-3.5 border-b border-slate-200">
          <div className="flex items-center gap-3">
            <div className="h-9 w-9 rounded-lg bg-blue-600 flex items-center justify-center">
              <Book className="h-4 w-4 text-white" />
            </div>
            <div>
              <div className="text-[15px] font-semibold text-slate-900">Knowledge Base</div>
              <div className="text-[12px] text-slate-500">
                Feeds the AI Diagnostic Assistant · {docs.length} documents · {taxonomy.categories.length} service categories
              </div>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Button
              size="sm"
              onClick={() => {
                setUploadPrefill(null);
                setUploadOpen(true);
              }}
              className="bg-blue-600 hover:bg-blue-700 text-white"
              data-testid="kb-add-document-btn"
            >
              <Plus className="h-3.5 w-3.5 mr-1.5" /> Upload document
            </Button>
            <button className="h-8 w-8 rounded-md hover:bg-slate-100 flex items-center justify-center text-white" onClick={onClose} data-testid="kb-close">
              <X className="h-6 w-6" />
            </button>
          </div>
        </div>

        {/* Tabs */}
        <div className="border-b border-slate-100 px-5">
          <div className="flex items-center gap-1">
            {[
              { id: "documents", label: `Documents (${docs.length})`, icon: <FileText className="h-3.5 w-3.5" /> },
              { id: "retrieval", label: "Retrieval Tester", icon: <Zap className="h-3.5 w-3.5" /> },
              { id: "taxonomy", label: "Taxonomy", icon: <Filter className="h-3.5 w-3.5" /> },
            ].map((t) => (
              <button
                key={t.id}
                onClick={() => setTab(t.id)}
                className={`flex items-center gap-1.5 px-3 py-2 text-[12.5px] font-medium border-b-2 -mb-px ${
                  tab === t.id ? "border-blue-600 text-blue-700" : "border-transparent text-slate-500 hover:text-slate-700"
                }`}
                data-testid={`kb-main-tab-${t.id}`}
              >
                {t.icon}
                {t.label}
              </button>
            ))}
          </div>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto p-5">
          {tab === "documents" ? (
            <div className="space-y-3">
              {/* Search + filters */}
              <div className="grid grid-cols-12 gap-2">
                <div className="col-span-4 relative">
                  <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-slate-400" />
                  <Input
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    placeholder="Search title, filename, version, source"
                    className="h-9 pl-8 text-[13px]"
                    data-testid="kb-search-input"
                  />
                </div>
                <div className="col-span-2">
                  <NativeSelect
                    value={filter.categoryId}
                    onChange={(v) => setFilter((f) => ({ ...f, categoryId: v || "" }))}
                    options={taxonomy.categories.map((c) => ({ value: c.id, label: c.name }))}
                    placeholder="All categories"
                    testId="kb-filter-category"
                  />
                </div>
                <div className="col-span-2">
                  <NativeSelect
                    value={filter.manufacturerId}
                    onChange={(v) => setFilter((f) => ({ ...f, manufacturerId: v || "" }))}
                    options={taxonomy.manufacturers.map((m) => ({ value: m.id, label: m.name }))}
                    placeholder="All manufacturers"
                    testId="kb-filter-manufacturer"
                  />
                </div>
                <div className="col-span-2">
                  <NativeSelect
                    value={filter.docType}
                    onChange={(v) => setFilter((f) => ({ ...f, docType: v || "" }))}
                    options={DOC_TYPES}
                    placeholder="All doc types"
                    testId="kb-filter-doctype"
                  />
                </div>
                <div className="col-span-2">
                  <NativeSelect
                    value={filter.status}
                    onChange={(v) => setFilter((f) => ({ ...f, status: v || "" }))}
                    options={[
                      { value: "indexed", label: "Indexed" },
                      { value: "processing", label: "Processing" },
                      { value: "pending", label: "Pending" },
                      { value: "error", label: "Error" },
                    ]}
                    placeholder="Any status"
                    testId="kb-filter-status"
                  />
                </div>
              </div>

              {loading ? (
                <div className="flex items-center justify-center py-16 text-slate-400">
                  <Loader2 className="h-5 w-5 animate-spin" />
                </div>
              ) : filteredDocs.length === 0 ? (
                <div className="border-2 border-dashed border-slate-200 rounded-lg py-16 text-center">
                  <BookOpen className="h-8 w-8 text-slate-300 mx-auto mb-2" />
                  <div className="text-[14px] font-medium text-slate-600 mb-1">No documents yet</div>
                  <div className="text-[12px] text-slate-500 mb-4">Upload your first service manual, bulletin, or spec sheet to train the AI.</div>
                  <Button
                    size="sm"
                    onClick={() => {
                      setUploadPrefill(null);
                      setUploadOpen(true);
                    }}
                    className="bg-blue-600 hover:bg-blue-700 text-white"
                  >
                    <Plus className="h-3.5 w-3.5 mr-1.5" /> Upload document
                  </Button>
                </div>
              ) : (
                <div className="border border-slate-200 rounded-lg overflow-hidden">
                  <table className="w-full text-[12.5px]">
                    <thead className="bg-slate-50 text-slate-500">
                      <tr>
                        <th className="text-left px-3 py-2 font-medium">Title</th>
                        <th className="text-left px-3 py-2 font-medium">Category</th>
                        <th className="text-left px-3 py-2 font-medium">Manufacturer</th>
                        <th className="text-left px-3 py-2 font-medium">Model(s)</th>
                        <th className="text-left px-3 py-2 font-medium">Version</th>
                        <th className="text-left px-3 py-2 font-medium">Effective</th>
                        <th className="text-left px-3 py-2 font-medium">Pages · Chunks</th>
                        <th className="text-left px-3 py-2 font-medium">Status</th>
                        <th className="w-[120px]"></th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100">
                      {filteredDocs.map((d) => (
                        <tr
                          key={d.id}
                          className={`hover:bg-slate-50 ${d.active ? "" : "opacity-60"}`}
                          data-testid={`kb-doc-row-${d.id}`}
                        >
                          <td className="px-3 py-2">
                            <button
                              className="text-left"
                              onClick={() => setDetailDoc(d)}
                              data-testid={`kb-doc-open-${d.id}`}
                            >
                              <div className="font-medium text-slate-800 hover:text-blue-700 truncate max-w-[240px]">{d.title}</div>
                              <div className="text-[10.5px] text-slate-500 flex items-center gap-1.5">
                                <span>{DOC_TYPE_LABEL[d.docType] || d.docType}</span>
                                {d.supersededByDocumentId && (
                                  <span className="text-amber-600 flex items-center gap-0.5">
                                    <History className="h-2.5 w-2.5" /> superseded
                                  </span>
                                )}
                                {d.supersedesDocumentId && (
                                  <span className="text-blue-600 flex items-center gap-0.5">
                                    <History className="h-2.5 w-2.5" /> replaces
                                  </span>
                                )}
                              </div>
                            </button>
                          </td>
                          <td className="px-3 py-2 text-slate-700">{nameOf("categories", d.categoryId)}</td>
                          <td className="px-3 py-2 text-slate-700">{nameOf("manufacturers", d.manufacturerId)}</td>
                          <td className="px-3 py-2 text-slate-700">
                            {(d.modelIds || []).length ? (
                              <span className="text-[11.5px]">
                                {(d.modelIds || []).slice(0, 2).map((mid) => nameOf("models", mid)).join(", ")}
                                {(d.modelIds || []).length > 2 ? ` +${d.modelIds.length - 2}` : ""}
                              </span>
                            ) : (
                              <span className="text-slate-400">—</span>
                            )}
                          </td>
                          <td className="px-3 py-2 text-slate-700">{d.version || "—"}</td>
                          <td className="px-3 py-2 text-slate-700">{d.effectiveDate || "—"}</td>
                          <td className="px-3 py-2 text-slate-700">
                            {d.pages || 0} · {d.chunkCount || 0}
                          </td>
                          <td className="px-3 py-2">
                            <StatusPill status={d.status} error={d.indexError} />
                          </td>
                          <td className="px-3 py-2">
                            <div className="flex items-center gap-1 justify-end">
                              <button
                                onClick={() => openReplace(d)}
                                className="text-[11px] text-blue-700 hover:underline"
                                title="Upload a new version"
                                data-testid={`kb-doc-replace-${d.id}`}
                              >
                                Replace
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          ) : tab === "retrieval" ? (
            <div className="h-full">
              <RetrievalTesterPanel taxonomy={taxonomy} />
            </div>
          ) : (
            <TaxonomyPanel taxonomy={taxonomy} refresh={load} />
          )}
        </div>
      </div>

      <UploadDialog
        open={uploadOpen}
        onClose={() => setUploadOpen(false)}
        taxonomy={taxonomy}
        prefill={uploadPrefill}
        existingDocs={docs}
        onUploaded={() => load()}
      />
      {detailDoc && (
        <DocumentDetail
          doc={detailDoc}
          taxonomy={taxonomy}
          onClose={() => setDetailDoc(null)}
          onChanged={load}
        />
      )}
    </div>
  );
}
